/**
 * Contract interest-rate floor / ceiling (T3-170, OPEN-ITEMS #81 — clamp-and-flag, decided 2026-10-20).
 *
 * IDENTICAL COPY of capitalmanagement-api/utils/rateBounds.js (ESM here). The SQL twin is
 * fn_rate_apply_bounds. Keep all three in lockstep: a breaching benchmark + spread ACCRUES AT THE
 * BOUND, and the uncapped figure is kept so the screen can show that the bound was binding.
 */

const toNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * @returns {{ rate: number|null, uncapped: number|null, bound: 'floor'|'ceiling'|null }}
 */
export const applyRateBounds = (rate, floor, ceiling) => {
  const r = toNum(rate);
  const f = toNum(floor);
  const c = toNum(ceiling);
  if (r === null) return { rate: null, uncapped: null, bound: null };
  const floored = f !== null && r < f ? f : r;
  if (c !== null && floored > c) return { rate: c, uncapped: r, bound: 'ceiling' };
  if (floored !== r) return { rate: floored, uncapped: r, bound: 'floor' };
  return { rate: r, uncapped: null, bound: null };
};

/** True when the contract's rate type can carry bounds (mirrors chk_rate_bounds_floating_only). */
export const rateTypeAllowsBounds = (rateType) => rateType === 'Variable' || rateType === 'Floating';

/** Validate a floor/ceiling pair for capture. Returns an error message or null. */
export const validateRateBounds = ({ rateType, floor, ceiling }) => {
  const f = toNum(floor);
  const c = toNum(ceiling);
  if (f === null && c === null) return null;
  if (!rateTypeAllowsBounds(rateType)) return 'A rate floor or ceiling can only be set on a Linked or Floating contract.';
  if ((f !== null && f < 0) || (c !== null && c < 0)) return 'Rate floor and ceiling cannot be negative.';
  if (f !== null && c !== null && f > c) return 'Rate floor cannot be higher than the rate ceiling.';
  return null;
};

/** One-line explanation for a bound that is binding, e.g. "Capped at 22.00% (benchmark + spread 24.50%)". */
export const describeBoundRate = (rate, uncapped) => {
  const r = toNum(rate);
  const u = toNum(uncapped);
  if (r === null || u === null || Math.abs(r - u) < 0.00005) return null;
  return `${u > r ? 'Capped' : 'Floored'} at ${r.toFixed(2)}% (benchmark + spread ${u.toFixed(2)}%)`;
};
